import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Star, Clock, Utensils } from 'lucide-react';

export const NewYork: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Best Chinese Food in New York City | Chinese Food Maps</title>
        <meta name="description" content="Discover the best Chinese restaurants in New York City. From dim sum in Manhattan's Chinatown to regional specialties in Flushing, explore NYC's diverse Chinese food scene." />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Best Chinese Food in New York City</h1>

        <div className="prose max-w-none text-gray-600 mb-12">
          <p className="text-lg leading-relaxed">
            New York City is home to multiple vibrant Chinatowns, each offering unique regional specialties and authentic dining experiences. From the historic dim sum parlors of Manhattan's Chinatown to the diverse regional cuisines of Flushing, Queens, NYC offers some of the most authentic Chinese food outside of Asia.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-8 text-black">Top Rated Chinese Food Restaurants</h2>

        <div className="grid gap-8">
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Manhattan Chinatown</h2>
            
            <div className="space-y-8">
              {/* Jing Fong */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Jing Fong</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.5</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>20 Elizabeth St, New York, NY 10013</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(212) 964-5256</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Classic Cantonese Dim Sum</li>
                    <li>Peking Duck</li>
                    <li>Seafood Dishes</li>
                    <li>Traditional Cart Service</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Har Gow (Crystal Shrimp Dumplings)</li>
                    <li>Siu Mai (Pork & Shrimp Dumplings)</li>
                    <li>BBQ Pork Buns</li>
                    <li>Egg Custard Tarts</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=20+Elizabeth+St+New+York+NY+10013`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:2129645256"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Joe's Shanghai */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Joe's Shanghai</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.7</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>9 Pell St, New York, NY 10013</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(212) 233-8888</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Shanghai-Style Soup Dumplings</li>
                    <li>Shanghainese Cuisine</li>
                    <li>Noodle Dishes</li>
                    <li>Traditional Cold Appetizers</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Crab & Pork Soup Dumplings</li>
                    <li>Spicy Szechuan-Style Beef</li>
                    <li>Shanghai Fried Noodles</li>
                    <li>Braised Pork Belly</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=9+Pell+St+New+York+NY+10013`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:2122338888"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Great NY Noodletown */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Great NY Noodletown</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.4</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>28 Bowery, New York, NY 10013</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(212) 349-0923</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Cantonese BBQ</li>
                    <li>Wonton Noodle Soup</li>
                    <li>Late Night Dining</li>
                    <li>Hong Kong Style Dishes</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Roast Duck</li>
                    <li>Salt & Pepper Shrimp</li>
                    <li>Ginger Scallion Lo Mein</li>
                    <li>Congee with Preserved Egg</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=28+Bowery+New+York+NY+10013`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:2123490923"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Wo Hop */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Wo Hop</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.3</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>17 Mott St, New York, NY 10013</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(212) 962-8617</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Classic Chinese-American Dishes</li>
                    <li>24-Hour Service</li>
                    <li>Large Portions</li>
                    <li>Historic Restaurant (Since 1938)</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Chow Fun</li>
                    <li>House Special Soup</li>
                    <li>Beef with Broccoli</li>
                    <li>Roast Pork Egg Foo Young</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=17+Mott+St+New+York+NY+10013`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:2129628617"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>

              {/* Nom Wah Tea Parlor */}
              <div className="border-b border-gray-200 pb-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold">Nom Wah Tea Parlor</h3>
                  <div className="flex items-center bg-yellow-100 px-3 py-1.5 rounded-full">
                    <Star size={18} className="text-yellow-500 mr-1" />
                    <span className="font-medium">4.6</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-3">
                  <MapPin size={18} className="text-red-600" />
                  <p>13 Doyers St, New York, NY 10013</p>
                </div>
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <Phone size={18} className="text-red-600" />
                  <p>(212) 962-6047</p>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Specialties:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Historic Dim Sum Restaurant (Since 1920)</li>
                    <li>Vintage Atmosphere</li>
                    <li>Traditional Dim Sum</li>
                    <li>Made-to-Order Dim Sum</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Must-Try Dishes:</h4>
                  <ul className="list-disc pl-5 text-gray-600 space-y-1">
                    <li>Original Egg Roll</li>
                    <li>House Special Rice Roll</li>
                    <li>Shrimp & Snow Pea Leaf Dumplings</li>
                    <li>Pan-Fried Noodles</li>
                  </ul>
                </div>
                <div className="flex gap-4 mt-6">
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=13+Doyers+St+New+York+NY+10013`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border-2 border-red-600 text-red-600 rounded-md hover:bg-red-600 hover:text-white transition-colors text-base font-medium"
                  >
                    Get Directions
                  </a>
                  <a
                    href="tel:2129626047"
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors gap-2 text-base font-medium"
                  >
                    <Phone size={18} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">NYC Chinese Food Fun Facts</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Historical Significance</h3>
                  <p className="text-gray-700">Manhattan's Chinatown is one of the oldest and largest Chinese communities outside of Asia, established in the 1870s.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Regional Diversity</h3>
                  <p className="text-gray-700">NYC's Chinese restaurants represent all eight major Chinese culinary traditions, from Cantonese to Sichuan.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Cultural Impact</h3>
                  <p className="text-gray-700">Many Chinese-American dishes were invented in NYC, including General Tso's Chicken and Fortune Cookies.</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Modern Evolution</h3>
                  <p className="text-gray-700">Flushing has surpassed Manhattan's Chinatown as NYC's largest and most diverse Chinese food destination.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-semibold text-red-800 mb-6">Best Times to Visit</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3">Peak Hours</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dim Sum: 10 AM - 2 PM (weekends)
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock size={18} className="text-red-600" />
                    Dinner: 6 PM - 9 PM
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Special Events</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Lunar New Year (Jan/Feb)
                  </li>
                  <li className="flex items-center gap-2">
                    <Utensils size={18} className="text-red-600" />
                    Mid-Autumn Festival (Sept/Oct)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};